<template>
  <div class="unpaid-bills-widget-container">
    <div v-if="loading" class="unpaid-bills-widget-loading">Loading...</div>
    <div v-else-if="error" class="unpaid-bills-widget-error">{{ error }}</div>
    <div v-else class="unpaid-bills-widget-content">
      <div class="unpaid-bills-widget-header">
        <div class="unpaid-bills-widget-header-label">Unpaid Bills</div>
        <div class="unpaid-bills-widget-header-value unpaid-bills-widget-negative">{{ formatCurrency(totalUnpaid) }}</div>
      </div>
      <div class="unpaid-bills-widget-details">
        <div class="unpaid-bills-widget-detail-row">
          <div class="unpaid-bills-widget-detail-label">Number of Bills</div>
          <div class="unpaid-bills-widget-detail-value">{{ unpaidBillsCount }}</div>
        </div>
        <div class="unpaid-bills-widget-detail-row">
          <div class="unpaid-bills-widget-detail-label">Overdue Bills</div>
          <div class="unpaid-bills-widget-detail-value unpaid-bills-widget-overdue">{{ overdueBillsCount }}</div>
        </div>
      </div>
      <div v-if="unpaidBillsCount > 0" class="unpaid-bills-widget-footer">
        <router-link :to="{ path: `/company/${companyId}/dashboard/vendors/bills`, query: { view: 'Bills' } }" class="unpaid-bills-widget-link">View all bills →</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/UnpaidBillsWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'UnpaidBillsWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      unpaidBillsCount: 0,
      totalUnpaid: 0,
      overdueBillsCount: 0,
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const bills = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Bills' });
        const billsArray = Array.isArray(bills) ? bills : [];
        
        // Filter unpaid bills (not voided, has balance > 0)
        const unpaidBills = billsArray.filter(bill => {
          const voided = bill.voided || bill.isVoided || (bill.status || '').toLowerCase() === 'cancelled';
          const balance = parseFloat(bill.balance || bill.remainingAmount || 0);
          return !voided && balance > 0;
        });
        
        this.unpaidBillsCount = unpaidBills.length;
        this.totalUnpaid = unpaidBills.reduce((sum, bill) => {
          return sum + parseFloat(bill.balance || bill.remainingAmount || 0);
        }, 0);
        
        // Count overdue bills
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.overdueBillsCount = unpaidBills.filter(bill => {
          const status = (bill.status || '').toLowerCase();
          const dueDate = bill.dueDate ? new Date(bill.dueDate) : null;
          
          // Explicitly marked as overdue
          if (status === 'overdue') {
            return true;
          }
          
          // Check if past due date
          if (dueDate && (status === 'open' || status === 'partial' || status === 'disputed' || status === 'on hold')) {
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < today;
          }
          
          return false;
        }).length;
      } catch (error) {
        logger.error('Error fetching unpaid bills:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

